import MarketingLayout from "@/components/MarketingLayout";
import { CodeBlock } from "@/components/CodeBlock";
import { skills } from "@/lib/skills";
import { Download, ExternalLink, Terminal, Package } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function Skills() {
  return (
    <MarketingLayout>
      <section className="container py-16">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Skills
          </h1>
          <p className="text-lg text-muted-foreground mb-10">
            Extend Double O with community skills. Each skill is a public
            GitHub repo you can install in one command.
          </p>

          {/* How-to section */}
          <div className="rounded-lg border border-border bg-card p-4 sm:p-6 mb-10 overflow-hidden">
            <div className="flex items-center gap-2 mb-4">
              <Terminal className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-semibold text-card-foreground">
                How to install a skill
              </h2>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-foreground mb-2">
                  1. Install a skill from any public GitHub repo
                </p>
                <CodeBlock
                  code="npx skills install <owner/repo>"
                  language="bash"
                />
              </div>

              <div>
                <p className="text-sm font-medium text-foreground mb-2">
                  2. List your installed skills
                </p>
                <CodeBlock code="npx skills list" language="bash" />
              </div>

              <div>
                <p className="text-sm font-medium text-foreground mb-2">
                  3. Remove a skill
                </p>
                <CodeBlock
                  code="npx skills remove <skill-name>"
                  language="bash"
                />
              </div>

              <p className="text-sm text-muted-foreground mt-4">
                Skills are pulled directly from public GitHub repositories. Browse the
                collection below and click the install command to copy it.
              </p>
            </div>
          </div>

          {/* Skills listing */}
          {skills.length > 0 ? (
            <div className="space-y-6">
              {skills.map((skill) => (
                <article
                  key={skill.name}
                  className="rounded-lg border border-border bg-card p-4 sm:p-6 transition-colors hover:bg-muted/40 overflow-hidden"
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-2">
                      <Package className="w-5 h-5 text-primary" />
                      <h2 className="text-xl font-semibold text-card-foreground">
                        {skill.name}
                      </h2>
                    </div>
                    <a
                      href={`https://github.com/${skill.repo}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-primary hover:underline shrink-0"
                    >
                      <ExternalLink className="w-4 h-4" />
                      GitHub
                    </a>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    {skill.description}
                  </p>
                  {skill.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {skill.tags.map((tag) => (
                        <Badge key={tag} variant="secondary">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <CodeBlock
                    code={`npx skills install ${skill.repo}`}
                    language="bash"
                  />
                </article>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-border bg-card p-12 text-center">
              <Download className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-card-foreground mb-2">
                Skills coming soon
              </h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                We're curating a collection of skills for Double O. Check back
                soon for installable extensions.
              </p>
            </div>
          )}
        </div>
      </section>
    </MarketingLayout>
  );
}
